import {
  Component,
  ElementRef,
  Inject,
  ViewChild,
  OnInit,
  AfterViewInit,
  OnDestroy,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { fabric } from "fabric";
import { decompressFrames, parseGIF } from "gifuct-js";
import { DcoCreativesService } from "src/app/core/services/dco/dco-creatives.service";

@Component({
  selector: 'app-creative-preview',
  templateUrl: './creative-preview.component.html',
  styleUrl: './creative-preview.component.scss'
})
export class CreativePreviewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild("previewCanvas", { static: true })
  previewCanvasRef!: ElementRef<HTMLCanvasElement>;

  private canvas!: fabric.Canvas;
  private gifTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  canvasWidth = 300;
  canvasHeight = 250;
  meta!: any;
  component!: string;
  creativePreview: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<CreativePreviewComponent>,
    private dcoCreativesService: DcoCreativesService
  ) {}

  ngOnInit(): void {
    this.dialogRef.updatePosition({
      top: "21em",
      left: "40%",
      right: "10em",
    });

    if (this.data?.width && this.data?.height) {
      this.canvasWidth = this.data.width;
      this.canvasHeight = this.data.height;
    }

    this.meta = this.data?.meta || this.data;
    this.component = this.data?.component || "";
  }

  ngAfterViewInit(): void {
    this.initCanvas();
    this.renderMeta();
  }

  private initCanvas(): void {
    this.canvas = new fabric.Canvas(this.previewCanvasRef.nativeElement, {
      backgroundColor: "#ffffff",
      preserveObjectStacking: true,
      selection: false,
    });
    this.canvas.setWidth(this.canvasWidth);
    this.canvas.setHeight(this.canvasHeight);
    this.canvas.off();
  }

  private renderMeta(): void {
    if (!this.meta) return;

    if (this.component === "dcoCreatives") {
      let previewData = {
        meta: this.meta,
        product_feed_id: this.data.product_feed_id,
      };
      this.dcoCreativesService
        .getCreativePreview(this.data.globalParams, previewData)
        .subscribe((res) => {
          this.creativePreview = {
            version: "5.3.0",
            objects: [...res.meta],
          };
          this.preview();
        },(err) => {
          this.dialogRef.close();
        }, () => {});
    } else {
      this.preview();
    }
  }
  private stripCrossOriginFromMeta(meta: any): any {
    if (!meta) return meta;
    const out = { ...meta };
    if (Array.isArray(out.objects)) {
      out.objects = out.objects.map((obj: any) =>
        this.stripCrossOriginFromObject(obj)
      );
    }
    return out;
  }

  private stripCrossOriginFromObject(obj: any): any {
    if (!obj) return obj;
    const copy = { ...obj };
    if (copy.type === "image") {
      copy.crossOrigin = null;
    }
    if (Array.isArray(copy.objects)) {
      copy.objects = copy.objects.map((o: any) =>
        this.stripCrossOriginFromObject(o)
      );
    }
    return copy;
  }

  private preview(): void {
    this.stopAllGifAnimations();
    this.canvas.clear();

    let previewMeta =
      this.component === "dcoCreatives" ? this.creativePreview : this.meta;
    const metaWithoutCors = this.stripCrossOriginFromMeta(previewMeta);

    this.canvas.loadFromJSON(metaWithoutCors, () => {
      const gifPromises: Promise<void>[] = [];

      this.canvas.getObjects().forEach((obj: any) => {
        obj.selectable = false;
        obj.evented = false;
        obj.lockMovementX = true;
        obj.lockMovementY = true;
        obj.lockScalingX = true;
        obj.lockScalingY = true;
        obj.lockRotation = true;
        obj.hasControls = false;
        obj.hasBorders = false;
        obj.editable = false;

        if (obj.type === 'textbox') {
          obj.height = undefined;
          obj.scaleX = 1;
          obj.scaleY = 1;
          obj.initDimensions();
        }

        obj.setCoords();

        const gifUrl = obj._gifUrl || obj.getSrc?.() || "";
        if (obj.type === "image" && gifUrl.toLowerCase().includes(".gif")) {
          gifPromises.push(this.replaceWithAnimatedGif(obj, gifUrl));
        }
      });

      this.canvas.renderAll();
      Promise.all(gifPromises).then(() => this.canvas.renderAll());
    });
  }

  private replaceWithAnimatedGif(fabricObj: any, gifUrl: string): Promise<void> {
    return new Promise(async (resolve) => {
      try {
        // Preserve current rendered box size and position
        const displayWidth = fabricObj.getScaledWidth();
        const displayHeight = fabricObj.getScaledHeight();
        const left = fabricObj.left;
        const top = fabricObj.top;
        const angle = fabricObj.angle;
        const originX = fabricObj.originX;
        const originY = fabricObj.originY;
  
        const response = await fetch(gifUrl);
        const buffer = await response.arrayBuffer();
        const gif = parseGIF(buffer);
        const frames = decompressFrames(gif, true);
  
        if (!frames.length) {
          resolve();
          return;
        }
  
        const gifWidth = frames[0].dims.width;
        const gifHeight = frames[0].dims.height;
  
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = gifWidth;
        compositeCanvas.height = gifHeight;
        const compositeCtx = compositeCanvas.getContext('2d')!;
  
        const frameCanvas = document.createElement('canvas');
        frameCanvas.width = gifWidth;
        frameCanvas.height = gifHeight;
        const frameCtx = frameCanvas.getContext('2d')!;
  
        const patchCanvas = document.createElement('canvas');
        const patchCtx = patchCanvas.getContext('2d')!;
  
        // Replace image source with animated frame canvas
        fabricObj.setElement(frameCanvas);
  
        // IMPORTANT: set intrinsic size to GIF natural size
        fabricObj.set({
          width: gifWidth,
          height: gifHeight,
  
          // Preserve the exact old visible size
          scaleX: displayWidth / gifWidth,
          scaleY: displayHeight / gifHeight,
  
          left,
          top,
          angle,
          originX,
          originY
        });
  
        fabricObj.dirty = true;
        fabricObj.setCoords();
  
        let frameIndex = 0;
  
        const renderFrame = () => {
          if (!this.canvas || !this.canvas.getObjects().includes(fabricObj)) return;
  
          const frame = frames[frameIndex];
  
          patchCanvas.width = frame.dims.width;
          patchCanvas.height = frame.dims.height;
  
          const patchData = new ImageData(
            new Uint8ClampedArray(frame.patch),
            frame.dims.width,
            frame.dims.height
          );
          patchCtx.clearRect(0, 0, patchCanvas.width, patchCanvas.height);
          patchCtx.putImageData(patchData, 0, 0);
  
          // Handle disposal
          if (frame.disposalType === 2) {
            compositeCtx.clearRect(0, 0, gifWidth, gifHeight);
          }
  
          compositeCtx.drawImage(patchCanvas, frame.dims.left, frame.dims.top);
  
          frameCtx.clearRect(0, 0, gifWidth, gifHeight);
          frameCtx.drawImage(compositeCanvas, 0, 0);
  
          fabricObj.dirty = true;
          this.canvas.requestRenderAll();
  
          frameIndex = (frameIndex + 1) % frames.length;
  
          // gifuct-js delay is usually in hundredths of a second
          const delay = frame.delay && frame.delay > 0 ? frame.delay * 10 : 100;
  
          const timeoutId = window.setTimeout(renderFrame, delay);
          this.gifTimeoutIds.push(timeoutId as any);
        };
  
        renderFrame();
        resolve();
  
      } catch (err) {
        console.error('[GIF] Failed to load:', gifUrl, err);
        resolve();
      }
    });
  }

  private stopAllGifAnimations(): void {
    this.gifTimeoutIds.forEach((id) => clearTimeout(id));
    this.gifTimeoutIds = [];
  }

  ngOnDestroy(): void {
    this.stopAllGifAnimations();
    this.canvas?.dispose();
  }
}
